import {
	Box,
	HStack,
	useToken,
} from '@chakra-ui/react';
import React from 'react';

interface ScrollableGridProps {
	rows?: number;
	items: React.ReactNode[];
	columnGap?: string;
	rowGap?: string;
	buttonSize: 'sm' | 'md' | 'lg' | 'phone-md' | 'phone-lg';
}

export function ScrollableGrid({
	rows,
	items,
	columnGap = '3',
	rowGap = '1.5',
	buttonSize
}: ScrollableGridProps) {
	const [columnGapValue] = useToken('space', [columnGap]);
	const [rowGapValue] = useToken('sizes', [rowGap]);
	const spacingRem = parseFloat(rowGapValue);

	const measureRef = React.useRef<HTMLDivElement>(null);
	const [rowHeight, setRowHeight] = React.useState<number>(0);

	React.useLayoutEffect(() => {
		if (!measureRef.current) return;

		const resizeObserver = new ResizeObserver((entries) => {
			const entry = entries[0];
			if (entry) {
				setRowHeight(entry.contentRect.height);
			}
		});

		resizeObserver.observe(measureRef.current);

		return () => resizeObserver.disconnect();
	}, []);

	const isHorizontal = rows === 1;
	const scrollPx = isHorizontal ? 10 : 0;
	const computedHeight =
		rows === undefined || rowHeight === 0
			? 'auto'
			: `${((rows * rowHeight) + ((rows - 1) * (spacingRem * 16)) + scrollPx) / 16}rem`;

	// Estos son los tamaños de los botones traducidos a px
	// TODO: Hay que traducir esto a REM de verdad para que si luego se cambia el font-size del html, se ajuste bien
	const buttonSizeMap = {
		sm: 15 /*rem*/ * 4,
		md: 22 /*rem*/ * 4,
		lg: 27 /*rem*/ * 4,
		'phone-md': 15 /*rem*/ * 4,
		'phone-lg': 20 /*rem*/ * 4,
	};

	return (
		<Box
			overflowX={isHorizontal ? 'auto' : 'hidden'}
			overflowY={isHorizontal ? 'hidden' : 'auto'}
			minH={computedHeight}
			height={computedHeight}
			w='full'
		>
			<HStack
				wrap={isHorizontal ? 'nowrap' : 'wrap'}
				columnGap={columnGapValue}
				rowGap={rowGapValue}
				display={isHorizontal ? 'flex' : 'grid'}
				gridTemplateColumns={isHorizontal ? undefined : `repeat(auto-fill, ${buttonSizeMap[buttonSize]}px)`}
				justifyContent={isHorizontal ? 'flex-start' : 'space-evenly'}
			>
				{items.map((node, i) => (
					<div
						key={i}
						ref={i === 0 ? measureRef : undefined}
						style={{ 'flexShrink': isHorizontal ? 0 : undefined }}
					>
						{node}
					</div>
				))}
			</HStack>
		</Box>
	);
}
